import { Label } from '@/components/ui/label';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import type { AnswerOptionCondition } from 'shared/types/due-diligence';

interface QuestionAnswerEditorProps {
  condition: AnswerOptionCondition;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
  availableQuestions: Array<{
    id: number;
    displayName: string;
    fieldName: string;
    answerOptions?: Array<{
      id?: number;
      label: string;
      value: string;
      statusColor: string;
      active: boolean;
    }>;
  }>;
  currentQuestionId?: number;
}

const getStatusColorClass = (color: string): string => {
  switch (color.toLowerCase()) {
    case 'green': return 'bg-green-500';
    case 'orange': return 'bg-orange-500';
    case 'red': return 'bg-red-500';
    default: return 'bg-gray-300';
  }
};

export function QuestionAnswerEditor({ condition, onUpdate, availableQuestions, currentQuestionId }: QuestionAnswerEditorProps) {
  const comparisonOptions = [
    { value: 'equals', label: 'Is gelijk aan' },
    { value: 'not_equals', label: 'Is niet gelijk aan' },
    { value: 'any_of', label: 'Is een van' },
  ];

  const filteredQuestions = availableQuestions.filter((q) => q.id !== currentQuestionId);
  const selectedQuestion = filteredQuestions.find((q) => q.id === condition.referencedQuestionId);
  const answerOptions = selectedQuestion?.answerOptions?.filter((opt) => opt.active) || [];

  const questionOptions = filteredQuestions.map((q) => ({
    value: String(q.id),
    label: q.displayName || q.fieldName,
  }));

  const answerOptionDropdownOptions = answerOptions.map((opt) => ({
    value: opt.id ? String(opt.id) : opt.value,
    label: (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${getStatusColorClass(opt.statusColor)}`} />
        <span>{opt.label}</span>
      </div>
    ),
  }));

  if (filteredQuestions.length === 0) {
    return (
      <div className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg border border-border text-center">
        <p className="font-medium mb-1">Geen andere vragen beschikbaar</p>
        <p>Voeg eerst andere vragen toe om deze conditie te kunnen gebruiken.</p>
      </div>
    );
  }

  const getExplanationText = () => {
    if (!condition.referencedQuestionId) return 'Selecteer een vraag om verder te gaan.';
    const qName = selectedQuestion?.displayName || 'de geselecteerde vraag';
    const comparison = condition.questionAnswerComparison || 'equals';
    const answer = answerOptions.find(
      (opt) => opt.id === condition.expectedOptionId || opt.value === condition.expectedOptionValue,
    )?.label || 'een antwoord';
    const compLabel = comparison === 'equals' ? 'gelijk is aan' : comparison === 'not_equals' ? 'niet gelijk is aan' : 'een van de opties is';
    return `Deze conditie is waar wanneer het antwoord op "${qName}" ${compLabel} "${answer}".`;
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Referentie vraag</Label>
        <SimpleDropdown
          value={condition.referencedQuestionId ? String(condition.referencedQuestionId) : ''}
          onChange={(value) => onUpdate({ referencedQuestionId: parseInt(value, 10), expectedOptionId: undefined, expectedOptionValue: undefined })}
          options={questionOptions}
          placeholder="Selecteer vraag..."
        />
      </div>

      {condition.referencedQuestionId && (
        <>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Vergelijkings type</Label>
            <SimpleDropdown
              value={condition.questionAnswerComparison || 'equals'}
              onChange={(value) => onUpdate({ questionAnswerComparison: value as AnswerOptionCondition['questionAnswerComparison'] })}
              options={comparisonOptions}
              placeholder="Selecteer type..."
            />
          </div>

          {answerOptions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Verwacht antwoord</Label>
              <SimpleDropdown
                value={condition.expectedOptionId ? String(condition.expectedOptionId) : condition.expectedOptionValue || ''}
                onChange={(value) => {
                  const numVal = parseInt(value, 10);
                  if (!isNaN(numVal)) {
                    const opt = answerOptions.find((o) => o.id === numVal);
                    onUpdate({ expectedOptionId: numVal, expectedOptionValue: opt?.value });
                  } else {
                    onUpdate({ expectedOptionValue: value });
                  }
                }}
                options={answerOptionDropdownOptions}
                placeholder="Selecteer antwoord..."
              />
            </div>
          )}
        </>
      )}

      <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border">
        <p className="font-medium mb-1">Uitleg:</p>
        <p>{getExplanationText()}</p>
      </div>
    </div>
  );
}
